import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, RadioGroup, ResultTable } from '../_fitnessHelpers';

const DRINKS = [
  // Beer sizes (5% ABV typical)
  { key: 'beer-12',    label: 'Beer — 12 oz can/bottle (5% ABV)',   ml: 355, abv: 5 },
  { key: 'beer-16',    label: 'Beer — 16 oz pint (5% ABV)',         ml: 473, abv: 5 },
  { key: 'beer-22',    label: 'Beer — 22 oz bomber (5% ABV)',       ml: 651, abv: 5 },
  { key: 'beer-40',    label: 'Beer — 40 oz large bottle (5% ABV)', ml: 1183, abv: 5 },
  { key: 'beer-ipa',   label: 'Craft IPA — 12 oz (7% ABV)',         ml: 355, abv: 7 },
  { key: 'beer-strong',label: 'Strong Beer — 12 oz (8% ABV)',       ml: 355, abv: 8 },
  // Wine sizes (12% ABV typical)
  { key: 'wine-5',     label: 'Wine — 5 oz standard glass (12% ABV)', ml: 148, abv: 12 },
  { key: 'wine-6',     label: 'Wine — 6 oz pour (12% ABV)',           ml: 177, abv: 12 },
  { key: 'wine-9',     label: 'Wine — 9 oz large glass (12% ABV)',    ml: 266, abv: 12 },
  { key: 'wine-bottle',label: 'Wine — 750 mL bottle (12% ABV)',       ml: 750, abv: 12 },
  { key: 'wine-fort',  label: 'Fortified wine (Port) — 3 oz (20% ABV)', ml: 89, abv: 20 },
  { key: 'champagne',  label: 'Champagne — 5 oz flute (12% ABV)',     ml: 148, abv: 12 },
  // Liquor / Spirits (40% ABV typical)
  { key: 'shot-1',     label: 'Spirits — 1 oz shot (40% ABV)',        ml: 30,  abv: 40 },
  { key: 'shot-15',    label: 'Spirits — 1.5 oz standard shot (40% ABV)', ml: 44, abv: 40 },
  { key: 'shot-2',     label: 'Spirits — 2 oz double shot (40% ABV)', ml: 59,  abv: 40 },
  { key: 'mixed',      label: 'Mixed drink — 6 oz (10% ABV)',         ml: 177, abv: 10 },
  { key: 'martini',    label: 'Martini — 4 oz (30% ABV)',             ml: 118, abv: 30 },
  { key: 'liqueur',    label: 'Liqueur — 1.5 oz (25% ABV)',           ml: 44,  abv: 25 },
  // Other
  { key: 'cider',      label: 'Hard Cider — 12 oz (5% ABV)',          ml: 355, abv: 5 },
  { key: 'malt',       label: 'Malt liquor — 12 oz (7% ABV)',         ml: 355, abv: 7 },
  { key: 'seltzer',    label: 'Hard seltzer — 12 oz (5% ABV)',        ml: 355, abv: 5 },
  { key: 'sake',       label: 'Sake — 6 oz (15% ABV)',                ml: 177, abv: 15 },
  { key: 'custom',     label: 'Custom drink',                          ml: 355, abv: 5 },
];
const ALC_DENSITY = 0.789;

export default function BACCalc() {
  const [tab, setTab] = useState(0);
  const [gender, setGender] = useState('male');
  const [lb, setLb] = useState(160);
  const [kg, setKg] = useState(73);
  const [drinkKey, setDrinkKey] = useState('beer-12');
  const [count, setCount] = useState(2);
  const [ml, setMl] = useState(355);
  const [abv, setAbv] = useState(5);
  const [hours, setHours] = useState(1);
  const clear = () => { setGender('male'); setLb(160); setKg(73); setDrinkKey('beer-12'); setCount(2); setMl(355); setAbv(5); setHours(1); };

  const c = useMemo(() => {
    const drink = DRINKS.find(d => d.key === drinkKey) || DRINKS[0];
    const wKg = tab === 0 ? (+lb||0)*0.453592 : +kg||0;
    const wG = wKg * 1000;
    const r = gender === 'male' ? 0.68 : 0.55;
    const drinkMl = drinkKey === 'custom' ? +ml||0 : drink.ml;
    const drinkAbv = drinkKey === 'custom' ? +abv||0 : drink.abv;
    const gramsPerDrink = drinkMl * (drinkAbv/100) * ALC_DENSITY;
    const totalGrams = gramsPerDrink * (+count||0);
    let bac = wG > 0 ? (totalGrams / (wG * r)) * 100 - 0.015 * (+hours||0) : 0;
    bac = Math.max(0, bac);
    let level = 'Sober';
    if (bac >= 0.30) level = 'Life-threatening';
    else if (bac >= 0.20) level = 'Severely impaired';
    else if (bac >= 0.08) level = 'Legally intoxicated (US)';
    else if (bac >= 0.05) level = 'Mildly impaired';
    else if (bac >= 0.02) level = 'Feeling relaxed';
    const timeToSober = bac > 0 ? bac / 0.015 : 0;
    return { bac, level, timeToSober, totalGrams };
  }, [tab, gender, lb, kg, drinkKey, count, ml, abv, hours]);

  const color = c.bac >= 0.08 ? 'text-red-700 bg-red-50' : c.bac >= 0.02 ? 'text-yellow-800 bg-yellow-50' : 'text-green-700 bg-green-50';

  return (
    <CalcShell look="cnet" title="Blood Alcohol Concentration (BAC) Calculator" subtitle="Estimate BAC using the Widmark formula — for educational use only.">
      <CalcNetPanel slug="bac" activeTab={tab} onTabChange={setTab} onClear={clear} right={
        <div>
          <div className="text-lg text-slate-800">Estimated BAC</div>
          <div className="text-2xl font-bold text-[#2f9c3f]" data-testid="bac-result">{c.bac.toFixed(3)} ({(c.bac*100).toFixed(3)}‰)</div>
          <div className={`p-3 rounded text-sm mt-3 font-semibold ${color}`}>{c.level}</div>
          <ResultTable testid="bac-detail-table" rows={[
            ['Grams of alcohol consumed', `${c.totalGrams.toFixed(1)} g`],
            ['Est. time until sober (BAC = 0)', `${c.timeToSober.toFixed(2)} hours`],
            ['US legal driving limit', '0.08'],
          ]}/>
        </div>
      }>
        <Row label="Gender"><RadioGroup name="bac-g" value={gender} onChange={setGender} options={[{value:'male',label:'male'},{value:'female',label:'female'}]}/></Row>
        <Row label="Weight">{tab === 0
          ? <UnitInput value={lb} onChange={setLb} unit="lb" testid="bac-wlb"/>
          : <UnitInput value={kg} onChange={setKg} unit="kg" testid="bac-wkg"/>}</Row>
        <Row label="Drink type"><select value={drinkKey} onChange={e => setDrinkKey(e.target.value)} data-testid="bac-drink" className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm">{DRINKS.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}</select></Row>
        {drinkKey === 'custom' && <>
          <Row label="Volume"><UnitInput value={ml} onChange={setMl} unit="mL" testid="bac-ml"/></Row>
          <Row label="ABV %"><UnitInput value={abv} onChange={setAbv} unit="%" testid="bac-abv"/></Row>
        </>}
        <Row label="Number of drinks"><UnitInput value={count} onChange={setCount} testid="bac-drinks"/></Row>
        <Row label="Time since first drink"><UnitInput value={hours} onChange={setHours} unit="hours" testid="bac-hours"/></Row>
      </CalcNetPanel>
    </CalcShell>
  );
}
